export interface jsonDataStructure {
  folders: Folder[];
  files: File[];
  containerName: string;
  parentFolder: string;
  currentContinuationToken: string;
  nextContinuationToken: string;
  previousContinuationToken: string;
  limit: number;
}

export class RequestDataQuery implements jsonDataStructure {
  folders: Folder[];
  files: File[];
  path: string;
  containerName: string;
  parentFolder: string;
  currentContinuationToken: string;
  nextContinuationToken: string;
  previousContinuationToken: string;
  limit: number;
  moreDataAvaiable: boolean;

  public constructor(
    aFolders: Folder[],
    aFiles: File[],
    aPath: string,
    aContainerName: string,
    aParentFolder: string,
    aCurrentToken: string,
    aNextToken: string,
    aPrevToken: string,
    aLimit: number,
    aMoreData: boolean
  ) {
    this.files = aFiles;
    this.folders = aFolders;
    this.path = aPath;
    this.containerName = aContainerName;
    this.parentFolder = aParentFolder;
    this.currentContinuationToken = aCurrentToken;
    this.nextContinuationToken = aNextToken;
    this.previousContinuationToken = aPrevToken;
    this.limit = aLimit;
    this.moreDataAvaiable = aMoreData;
  }
}

export class Folder {
  name: string;
  files?: File[];
  filesCount?: number;

  public constructor(aName: string, aFiles?: File[], aCount?: number) {
    this.name = aName;
    this.files = aFiles;
    this.filesCount = aCount;
  }
}

export class File {
  name: string;
  fileGuid: string;
  fileSize: number;
  metaData: MetadataCollection;
  folderName: string;
  modifiedTime: string;
  version?: FileVersions;

  public constructor(
    aName: string,
    aSize: number,
    aMetaData: MetadataCollection,
    aFileGuid: string,
    aFolderName: string,
    aModifiedTime: string
  ) {
    this.name = aName;
    this.fileSize = aSize;
    this.metaData = aMetaData;
    this.fileGuid = aFileGuid;

    this.folderName = aFolderName;
    if (this.folderName === undefined) {
      this.folderName = "";
    }

    this.modifiedTime = aModifiedTime;
    if (this.modifiedTime === undefined) {
      this.modifiedTime = "";
    }
  }
}

class FileVersions {
  isLatestVersion: boolean;
  creationTime?: string;
  //other parameters

  public constructor(aVersion: boolean) {
    this.isLatestVersion = aVersion;
  }
}

export class MetadataCollection {
  metadataFromTemplate: { [key: string]: string };
  otherMetadata: { [key: string]: string };

  public constructor(
    metadataTemplate: MetadataTemplateItem[],
    anyMetadata: { [key: string]: string }
  ) {
    this.metadataFromTemplate = {};
    this.otherMetadata = {};
    metadataTemplate.map((item) => {
      if (item.key in anyMetadata) {
        if (item.readOnly) {
          this.metadataFromTemplate[item.key] = item.defaultValue;
        } else {
          this.metadataFromTemplate[item.key] = anyMetadata[item.key];
        }
      } else {
        if (item.required) {
          this.metadataFromTemplate[item.key] = "";
        }
      }
    });
    Object.entries(anyMetadata).forEach(([key, value]) => {
      if (!metadataTemplate.map((item) => item.key).includes(key)) {
        this.otherMetadata[key] = value;
      }
    });
  }
}

export class MetadataItem {
  key: string;
  value: string;
  isIndexed: boolean;

  public constructor(aKey: string, aValue: string) {
    this.key = aKey;
    this.value = aValue;
    this.isIndexed = true;
  }
}

export class MetadataRowItem {
  itemTemplate: MetadataTemplateItem;
  item: MetadataItem;
  id: number;

  public constructor(
    aKey: string,
    aValue: string,
    aID: number,
    isRequired: boolean = false,
    isReadOnly: boolean = false,
    aDataType: string = "string",
    isFromConfiguration: boolean = false
  ) {
    this.itemTemplate = new MetadataTemplateItem(
      aKey,
      isRequired,
      aValue,
      isReadOnly,
      aDataType,
      isFromConfiguration
    );
    this.item = new MetadataItem(aKey, aValue);
    this.id = aID;
  }
}

export class MetadataTemplateItem {
  key: string;
  required: boolean;
  defaultValue: string;
  readOnly: boolean;
  dataType: string;
  fromConfiguration: boolean;

  public constructor(
    aKey: string,
    isRequired: boolean,
    aDefaultValue: string,
    isReadOnly: boolean,
    aDataType: string,
    isFromConfiguration: boolean
  ) {
    this.key = aKey;
    this.readOnly = isReadOnly;
    this.required = isRequired;
    this.defaultValue = aDefaultValue;
    this.dataType = aDataType;
    this.fromConfiguration = isFromConfiguration;
  }
}

export class FilePart {
  filePartNumber: number;
  partGuid: string;

  public constructor(aFilePartNumber: number, aPartGuid: string) {
    this.filePartNumber = aFilePartNumber;
    this.partGuid = aPartGuid;
  }
}

export const emptyRequestDataProperties = {
  EMPTY_FOLDERS: [] as Folder[],
  EMPTY_FILES: [] as File[],
  EMPTY_PATH: "",
  EMPTY_CONTAINER: "",
  EMPTY_PARENT_FOLDER: "",
  EMPTY_CURRENT_TOKEN: "",
  EMPTY_NEXT_TOKEN: "",
  EMPTY_PREVIOUS_TOKEN: "",
  EMPTY_LIMIT: 0,
  EMPTY_OFFSET: "0",
};

export class UploadProgressEvent {
  fileName?: string;
  fileSize: number = 0;
  dataAlreadyUploaded: number = 0;
  loaded: number = 0;
  rate?: number;

  public constructor(
    aFileName: string,
    aFileSize: number,
    aDataAlreadyUploaded: number,
    aLoaded: number,
    aRate?: number
  ) {
    this.fileName = aFileName;
    this.fileSize = aFileSize;
    this.dataAlreadyUploaded = aDataAlreadyUploaded;
    this.loaded = aLoaded;
    this.rate = aRate;
  }
}

export class DownloadProgressEvent {
  fileSize: number;
  loaded: number;
  rate?: number;

  public constructor(aFileSize: number, aLoaded: number, aRate?: number) {
    this.fileSize = aFileSize;
    this.loaded = aLoaded;
    this.rate = aRate;
  }
}

export interface ContainersList {
  containers: ContainerData[];
}
export class ContainerData {
  containerName: string;
  folderStructure: ContainerFolderStructure;
  metadataTemplate: MetadataTemplateItem[];

  public constructor(
    aContainerName: string,
    aFolderStructure: string,
    aMetadataTemplate: MetadataTemplateItem[]
  ) {
    this.containerName = aContainerName;
    this.folderStructure = new ContainerFolderStructure(aFolderStructure);
    this.metadataTemplate = aMetadataTemplate;
  }
}

type singleEntry = { key: string; value: string };

export class FolderStructureParamsDictionary {
  entries: { [key: string]: string };
  isDictionaryComplete: boolean;

  public constructor(newEntries: singleEntry[]) {
    this.entries = {};
    for (const entry of newEntries) {
      this.entries[entry.key] = entry.value;
    }
    if (!newEntries) {
      this.isDictionaryComplete = false;
    } else {
      this.isDictionaryComplete = this.areValuesValid();
    }
  }

  areValuesValid = (): boolean => {
    for (const [key, value] of Object.entries(this.entries)) {
      if (!value) {
        return false;
      }
    }
    return true;
  };

  public setValue = (key: string, value: string) => {
    this.entries[key] = value;
    this.isDictionaryComplete = this.areValuesValid();
  };

  public getAllKeys = (): string[] => {
    return Object.keys(this.entries);
  };

  public getCompletedFolderPattern = (): string => {
    if (this.isDictionaryComplete) {
      const values = Object.values(this.entries);
      return values.join("/");
    } else {
      return "";
    }
  };
}

export class ContainerFolderStructure {
  requiredFolderParams: string[];

  public constructor(folderStructure: string) {
    this.requiredFolderParams = folderStructure
      .split("/")
      .filter((element) => element !== "");
  }

  public getFolderStructureParamsDictionary(
    values: string[]
  ): FolderStructureParamsDictionary {
    if (values.length !== this.requiredFolderParams.length && values.length) {
      console.error("Number of provided values doesn't match number of keys");
      return new FolderStructureParamsDictionary([]);
    } else {
      const dictionaryEntries: singleEntry[] = [];
      for (let i = 0; i < this.requiredFolderParams.length; i++) {
        dictionaryEntries.push({
          key: this.requiredFolderParams[i],
          value: values[i] ? values[i] : "",
        });
      }

      return new FolderStructureParamsDictionary(dictionaryEntries);
    }
  }
}

export enum Comparison {
  GreaterThan,
  GreaterThanOrEqual,
  LowerThan,
  LowerThanOrEqual,
  Equals,
  Like
}

export enum Field {
  FileName,
  FolderName,
  FileSize,
  CreationTime,
  ModificationDate
}

export class FilterField {
  field: Field;
  comparison: Comparison;
  valueOrPattern: string;

  public constructor(
    aField: Field,
    aComparison: Comparison,
    aValueOrPattern: string,
  ){
    this.field = aField;
    this.comparison = aComparison;
    this.valueOrPattern = aValueOrPattern;
  }
}

export class FilterFieldToString {
  field: string;
  comparing: string;
  valueOrPattern: string;

  public constructor(
    aFilterField: FilterField
  ){
    this.field = Field[aFilterField.field];
    this.comparing = Comparison[aFilterField.comparison];
    this.valueOrPattern = aFilterField.valueOrPattern;
  }
}

export interface FileTrackData {
  fileName: string;
  fileGuid: string;
  fileSize: number;
  folderName: string;
  containerName: string;
  totalTime: number;
}

export type FailureFileTrackData = FileTrackData & {
  error: any;
}
